import { createStore } from './store-factory';
import { logger } from '../utils/logger';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
    id: string;
    type: NotificationType;
    header?: string;
    content: string;
    dismissible?: boolean;
    autoDismiss?: boolean;
    dismissAfter?: number; // milliseconds
}

interface NotificationState {
    notifications: Notification[];
    nextId: number;
    addNotification: (
        type: NotificationType,
        content: string,
        header?: string,
        options?: {
            dismissible?: boolean;
            autoDismiss?: boolean;
            dismissAfter?: number;
        }
    ) => string;
    removeNotification: (id: string) => void;
    clearAll: () => void;
    success: (content: string, header?: string) => string;
    error: (content: string, header?: string) => string;
    warning: (content: string, header?: string) => string;
    info: (content: string, header?: string) => string;
}

export const useNotificationStore = createStore<NotificationState>('notification', (set, get) => ({
    notifications: [],
    nextId: 1,

    addNotification: (type, content, header, options) => {
        const { notifications, nextId } = get();

        // Check for duplicate notifications (same type and content)
        const isDuplicate = notifications.some(
            n => n.type === type && n.content === content && n.header === header
        );

        // If duplicate exists, don't add it again
        if (isDuplicate) {
            logger.info('Duplicate notification prevented:', { type, content, header });
            return notifications.find(n => n.type === type && n.content === content && n.header === header)?.id || '';
        }

        const id = `notification-${nextId}`;

        const notification: Notification = {
            id,
            type,
            header,
            content,
            dismissible: options?.dismissible ?? true,
            autoDismiss: options?.autoDismiss ?? (type === 'success' || type === 'info'),
            dismissAfter: options?.dismissAfter ?? 5000,
        };

        set({
            notifications: [...notifications, notification],
            nextId: nextId + 1
        });

        // Auto-dismiss if configured
        if (notification.autoDismiss) {
            setTimeout(() => {
                get().removeNotification(id);
            }, notification.dismissAfter);
        }

        return id;
    },

    removeNotification: (id) => {
        const { notifications } = get();
        set({ notifications: notifications.filter(n => n.id !== id) });
    },

    clearAll: () => {
        set({ notifications: [] });
    },

    success: (content, header = 'Success') => {
        return get().addNotification('success', content, header, {
            dismissible: true,
            autoDismiss: true,
            dismissAfter: 5000, // Auto-dismiss after 5 seconds
        });
    },

    error: (content, header = 'Error') => {
        return get().addNotification('error', content, header, {
            dismissible: true,
            autoDismiss: true,
            dismissAfter: 5000, // Auto-dismiss after 5 seconds
        });
    },

    warning: (content, header = 'Warning') => {
        return get().addNotification('warning', content, header, {
            dismissible: true,
            autoDismiss: false,
        });
    },

    info: (content, header) => {
        return get().addNotification('info', content, header);
    },
}));